import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Outlet,
  useMatch,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import axiosClient from "../../services/apiClient";
import { addAccessTokenInterceptor } from "../../services/apiClient";
import DateRangeSelector from "../../components/DateRangeSelector";
import { buildEventsAllBuildings } from "./unitEventsUtils";
import { NAVBAR_HEIGHT } from "../../components/Navbar/Navbar";
import ErrorState from "../../components/ui/ErrorState";

export type Building = {
  LatLong: [number, number];
  buildingId: string;
  name: string;
};

export type TempUnit = "C" | "F";

export type InsightsContext = {
  buildings: Building[];
  buildingsLoading: boolean;
  buildingsError: string | null;
  refetchBuildings: () => Promise<void>;
  tenantName: string | null;
  startDate: string | null;
  endDate: string | null;
  searchQuery: string;
  tempUnit: TempUnit;
  setTempUnit: (u: TempUnit) => void;
};

export function useInsightsCtx() {
  return useOutletContext<InsightsContext>();
}

export default function InsightsLayout() {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const sensorsMatch = useMatch(
    "/insights/properties/:buildingId/sensors/:sensorId"
  );
  const propertyMatch = useMatch("/insights/properties/:buildingId");

  const onBuildingsList = !sensorsMatch && !propertyMatch;
  const onSensors = !!propertyMatch && !sensorsMatch;
  const onSensorDetail = !!sensorsMatch;

  const [buildings, setBuildings] = useState<Building[]>([]);
  const [buildingsLoading, setBuildingsLoading] = useState(false);
  const [buildingsError, setBuildingsError] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [searchByPath, setSearchByPath] = useState<Record<string, string>>({});
  const [tempUnit, setTempUnitState] = useState<TempUnit>(() => {
    const saved =
      typeof window !== "undefined"
        ? window.localStorage.getItem("cts.tempUnit")
        : null;
    return saved === "C" || saved === "F" ? saved : "F";
  });
  const setTempUnit = useCallback((u: TempUnit) => {
    setTempUnitState(u);
    try {
      window.localStorage.setItem("cts.tempUnit", u);
    } catch {
      // ignore storage failures (private mode, etc.)
    }
  }, []);

  const searchKey = onBuildingsList
    ? "buildings"
    : onSensorDetail
      ? "sensorDetail"
      : "sensors";
  const searchQuery = searchByPath[searchKey] ?? "";
  const setSearchQuery = (v: string) =>
    setSearchByPath((m) => ({ ...m, [searchKey]: v }));

  const handleIntervalChange = useCallback((s: string, e: string) => {
    setStartDate(s);
    setEndDate(e);
  }, []);

  const fetchTenant = useCallback(async () => {
    try {
      const resp = await axiosClient.get("tenants");
      setTenantName(resp.data?.name ?? null);
    } catch (err) {
      console.warn("Tenant lookup failed; rendering without org name.", err);
      setTenantName(null);
    }
  }, []);

  const fetchBuildings = useCallback(async () => {
    setBuildingsError(null);
    setBuildingsLoading(true);
    try {
      const resp = await axiosClient.get("sensor-groups?pageSize=10");
      const data = resp.data?.data ?? [];
      setBuildings(data.length ? buildEventsAllBuildings(data) : []);
    } catch (err) {
      console.error("Failed to load buildings", err);
      setBuildingsError("Unable to load your properties. Please try again.");
    } finally {
      setBuildingsLoading(false);
    }
  }, []);

  useEffect(() => {
    addAccessTokenInterceptor(getAccessTokenSilently);
    fetchTenant();
    fetchBuildings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently]);

  // Find current building/sensor for breadcrumb labels
  const currentBuildingId = sensorsMatch?.params.buildingId ?? propertyMatch?.params.buildingId;
  const currentBuilding = useMemo(
    () => buildings.find((b) => b.buildingId === currentBuildingId),
    [buildings, currentBuildingId]
  );

  const ctx: InsightsContext = {
    buildings,
    buildingsLoading,
    buildingsError,
    refetchBuildings: fetchBuildings,
    tenantName,
    startDate,
    endDate,
    searchQuery,
    tempUnit,
    setTempUnit,
  };

  return (
    <main
      className="min-h-screen bg-surface text-white pb-24"
      style={{ paddingTop: NAVBAR_HEIGHT + 24 }}
    >
      <div className="max-w-screen-2xl mx-auto px-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <DateRangeSelector setInterval={handleIntervalChange} />
          <div className="flex items-center gap-3">
            <div
              className="inline-flex items-center rounded-control border border-white/15 overflow-hidden text-sm"
              role="group"
              aria-label="Temperature unit"
            >
              {(["F", "C"] as TempUnit[]).map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => setTempUnit(u)}
                  aria-pressed={tempUnit === u}
                  className={
                    tempUnit === u
                      ? "bg-primary text-ink font-semibold px-3 py-1.5"
                      : "text-white/80 px-3 py-1.5 hover:bg-white/10"
                  }
                >
                  °{u}
                </button>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-white/80">
              <span className="text-ink-3">Search</span>
              <input
                type="search"
                aria-label={
                  onBuildingsList
                    ? "Search properties"
                    : onSensorDetail
                      ? "Search this sensor"
                      : "Search sensors"
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-surface-100 text-white border border-white/15 rounded-control px-3 py-1.5 w-64 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/40"
              />
            </label>
          </div>
        </div>

        {(onSensors || onSensorDetail) && (
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-3 mb-5"
          >
            <button
              type="button"
              onClick={() => {
                if (onSensorDetail && currentBuildingId) {
                  navigate(`/insights/properties/${currentBuildingId}`);
                } else {
                  navigate("/insights");
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-chip border border-primary/45 bg-primary/15 text-primary hover:bg-primary/25 hover:border-primary/70 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            >
              <ArrowBackIosIcon fontSize="small" />
              <span className="font-semibold">
                {onSensorDetail ? "Back to Sensors" : "Back to Properties"}
              </span>
            </button>
            {!onSensorDetail && (
              <span className="text-white text-2xl font-semibold truncate max-w-[60ch]">
                {currentBuilding?.name || "Selected property"}
              </span>
            )}
          </nav>
        )}

        {buildingsError && onBuildingsList ? (
          <ErrorState message={buildingsError} onRetry={fetchBuildings} />
        ) : (
          <Outlet context={ctx} />
        )}
      </div>
    </main>
  );
}
